import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import IconAlert from '@/components/common/IconAlert';
import { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import Select, { type OptionProps } from 'react-select';
import {
  useGetDataFromScMutation,
  useGetHostNamesQuery
} from '@/app/KeywordResearch/api/gscApi';
import { GscHostName } from '@/app/KeywordResearch/types/gscApiTypes';
import { object } from 'yup';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import GSCSettingsDrawer from './GSCSettingsDrawer';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
import { Formik } from 'formik';
import classNames from 'classnames';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';

type SelectOption = {
  label: string;
  value: string;
};

const getGscSelectStyles = (hasError: boolean, borderRadius: string) => ({
  control: (baseStyles: any) => ({
    ...baseStyles,
    direction: 'ltr !important',
    paddingLeft: '10px',
    paddingRight: '0',
    ...(hasError && {
      borderColor: 'var(--falcon-form-invalid-border-color) !important'
    }),
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important',
    width: '100% !important',
    cursor: 'pointer',
    borderRadius
  }),
  input: (baseStyles: any) => ({
    ...baseStyles,
    direction: 'ltr !important',
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important'
  })
});

type KeywordGscFormValues = {
  account: SelectOption | null;
  domain: SelectOption | null;
};

type KeywordGscFormProps = {
  noHostName?: boolean;
};

const validationSchema = object().shape({
  account: object().required('اکانت رو انتخاب کنید.'),
  domain: object().required('دامنه رو ابتدا انتخاب کنید.')
});

type SubmitAlertType = 'processing' | 'queued' | null;

const SUBMIT_ALERT_MESSAGES: Record<Exclude<SubmitAlertType, null>, string> = {
  processing:
    'درخواست شما با موفقیت ارسال شد فرایند دریافت داده ها ممکن هست ۱۰ دقیقه تا ۲۴ ساعت طول بکشد بعد از اتمام به شما در بخش اعلان های سایت اطلاع داده میشود',
  queued:
    'درخواست شما در صف برای دریافت اطلاعات قرار گرفت فرایند دریافت داده ها ممکن هست ۱۰ دقیقه تا ۲۴ ساعت طول بکشد بعد از اتمام به شما در بخش اعلان های سایت اطلاع داده میشود'
};

function KeywordGscForm({ noHostName }: KeywordGscFormProps) {
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);
  const [isGscSettingsOpen, setIsGscSettingsOpen] = useState(false);
  const [submitAlert, setSubmitAlert] = useState<SubmitAlertType>(null);
  const { data: hostNames, isFetching: isFetchingHostNames } =
    useGetHostNamesQuery();
  const { domain_slug } = useDomain();

  const [refreshUserGscHistory, { isLoading: isLoadingRefreshGsc }] =
    useGetDataFromScMutation();

  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  return (
    <Formik<KeywordGscFormValues>
      initialValues={{
        account:
          hostNames?.data && hostNames.data.length > 0
            ? {
                label: hostNames.data[0].account_title,
                value: hostNames.data[0].account_id
              }
            : null,
        domain: null
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={values => {
        if (!values.domain) return;

        refreshUserGscHistory(values.domain.value)
          .unwrap()
          .then(response => {
            const payload = response.data;
            setSubmitAlert(null);

            if (!payload?.status) {
              return;
            }

            if (payload.error_code === 403) {
              errorMessage(
                `اکانت ${values.account?.label} دسترسی لازم برای گرفتن اطلاعات از سرچ کنسول رو نداره، در صورت نیاز لطفا دوباره دسترسی بدهید.`
              );
              return;
            }

            if (payload.error_code === 409) {
              warningMessage(
                'اطلاعات این دامنه به‌روزرسانی شده است. لطفاً برای درخواست مجدد، ۲۴ ساعت بعد تلاش کنید.'
              );
              return;
            }

            if (payload.status === 'not found host name') {
              errorMessage('دامنه انتخاب شده نامعتبر است.');
              return;
            }

            if (payload.status === 'duplicated request') {
              warningMessage(
                'درخواست برای این دامنه تکراری است. لطفا برای درخواست مجدد 24 ساعت بعد تلاش کنید.'
              );
              return;
            }

            if (payload.status === 'processing') {
              setSubmitAlert('processing');
              return;
            }

            if (payload.status === 'queued') {
              setSubmitAlert('queued');
              return;
            }

            successMessage(' عملیات با موفقیت انجام شد.');
          })
          .catch(err => {
            console.error('Error fetching data:', err);
            errorMessage('در انجام عملیات خطایی رخ داد.');
          });
      }}
    >
      {({ values, setFieldValue, errors, submitForm }) => (
        <form
          onSubmit={e => {
            e.preventDefault();
            submitForm();
          }}
        >
          <GSCSettingsDrawer
            isOpen={isGscSettingsOpen}
            setIsOpen={setIsGscSettingsOpen}
            account={values.account}
            setFieldValue={setFieldValue}
            hostNames={hostNames}
          />
          {noHostName && (
            <IconAlert show={true} variant="warning" className="mt-2 mb-4">
              <p className="mb-0 text-justify pe-3">
                اکانت متصل شده به گوگل دامنه ای در سرچ کنسول نداره،
              </p>
            </IconAlert>
          )}
          {submitAlert && (
            <IconAlert show={true} variant="info" className="mt-2 mb-4">
              <p className="mb-0 text-justify pe-3">
                {SUBMIT_ALERT_MESSAGES[submitAlert]}
              </p>
            </IconAlert>
          )}
          <div
            className={classNames({
              'input-group position-relative mt-3 input-group-md': isLgUp,
              'd-flex flex-column gap-2 w-100': !isLgUp
            })}
          >
            <div style={{ width: isLgUp ? '150px' : '100%', zIndex: 5 }}>
              <Select<SelectOption>
                menuShouldScrollIntoView={true}
                closeMenuOnSelect={true}
                isSearchable={true}
                onChange={e => {
                  setSubmitAlert(null);
                  setFieldValue('account', e);
                  setFieldValue('domain', null);
                }}
                value={values.account}
                options={
                  hostNames?.data
                    ? Array.from(
                        hostNames.data
                          .reduce((map, host) => {
                            if (!map.has(host.account_title)) {
                              map.set(host.account_title, {
                                label: host.account_title,
                                value: host.account_id
                              });
                            }
                            return map;
                          }, new Map<string, SelectOption>())
                          .values()
                      )
                    : []
                }
                classNamePrefix="react-select"
                maxMenuHeight={700}
                placeholder={isFetchingHostNames ? '...' : 'اکانت'}
                isDisabled={isFetchingHostNames}
                noOptionsMessage={() => 'اکانتی یافت نشد!!!'}
                styles={getGscSelectStyles(
                  !!errors.account,
                  isLgUp ? '0px 5px 5px 0px' : '5px'
                )}
                components={{
                  Option: OptionLayout
                }}
              />
            </div>
            <div style={{ width: isLgUp ? '250px' : '100%', zIndex: 4 }}>
              <Select<SelectOption>
                menuShouldScrollIntoView={true}
                closeMenuOnSelect={true}
                isSearchable={true}
                onChange={e => {
                  setSubmitAlert(null);
                  setFieldValue('domain', e);
                }}
                value={values.domain}
                options={
                  hostNames?.data
                    ? hostNames.data
                        .filter(
                          (host): host is GscHostName & { host_name_id: string } =>
                            !!host.host_name_id &&
                            host.host_name_id !== 'NULL_MIZTOOL'
                        )
                        .filter(
                          host => host.account_title === values.account?.label
                        )
                        .map(host => ({
                          label: host.host_name,
                          value: host.host_name_id
                        }))
                    : []
                }
                classNamePrefix="react-select"
                maxMenuHeight={700}
                placeholder={
                  isFetchingHostNames
                    ? 'درحال دریافت دامنه ها...'
                    : 'دامنه مدنظرت رو انتخاب کن'
                }
                isDisabled={isFetchingHostNames}
                noOptionsMessage={() => 'دامنه ای یافت نشد!!!'}
                styles={getGscSelectStyles(
                  !!errors.domain,
                  isLgUp ? '0px 0px 0px 0px' : '5px'
                )}
                components={{
                  Option: OptionLayout
                }}
              />
            </div>
            <div className="d-flex gap-1">
              <ChildrensTooltip
                placement="top"
                className="p-0"
                html={isAccessAllowed() ? '' : 'شما دسترسی لازم را ندارید'}
              >
                <Button
                  style={{
                    borderRadius: isLgUp ? '5px 0 0 5px' : '5px',
                    zIndex: 30
                  }}
                  onClick={() => setIsGscSettingsOpen(true)}
                  disabled={!isAccessAllowed()}
                  variant="outline-secondary"
                  title="تنظیمات GSC"
                  className="border-300 d-flex align-items-center h-100"
                >
                  <FontAwesomeIcon icon="cog" />
                </Button>
              </ChildrensTooltip>

              <Button
                type="submit"
                disabled={isLoadingRefreshGsc}
                variant="falcon-primary"
                className="border-300 flex-1 rounded-2 justify-content-center d-flex align-items-center"
              >
                <span className="ms-1">
                  {isLoadingRefreshGsc ? (
                    <>
                      <Spinner
                        className="me-1"
                        style={{
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      درحال دریافت
                    </>
                  ) : (
                    <>گرفتن کلمات از GSC</>
                  )}
                </span>
                <HelpTooltip
                  color={getColor('black')}
                  placement="top"
                  html={
                    <>
                      با زدن این گزینه، کلمات دامنه انتخاب شده از سرچ کنسول
                      گوگل، گرفته میشه؛ و سپس در جدول زیر بعد بارگذاری قابل
                      مشاهده هست.
                    </>
                  }
                />
              </Button>
            </div>
          </div>
          <Form.Control.Feedback
            className="d-block flex-1 m-0 ms-2"
            type={errors.account || errors.domain ? 'invalid' : 'valid'}
          >
            {errors.account || errors.domain}&nbsp;
          </Form.Control.Feedback>
        </form>
      )}
    </Formik>
  );
}

export default KeywordGscForm;

function OptionLayout(props: OptionProps<SelectOption>) {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
        .customized-searchbox-option:hover{
          background-color:var(--falcon-gray-link-hover-300) !important;
        }
        .customized-searchbox-option{
          background-color:var(--falcon-tertiary-bg) !important;
        }
      `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          direction: 'ltr',
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1 customized-searchbox-option"
      >
        <div className="fs-0 text-900 d-flex justify-content-between">
          {props.data.label}
        </div>
      </div>
    </>
  );
}
